import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save, Calendar, Flag, FileText } from 'lucide-react';

const TaskForm = ({ task, onSubmit, onClose }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      priority: 'medium',
      due_date: '',
      completed: false
    }
  });

  useEffect(() => {
    if (task) {
      setValue('title', task.title);
      setValue('description', task.description || '');
      setValue('priority', task.priority);
      setValue('completed', task.completed);
      
      if (task.due_date) {
        // Format date for input field
        const date = new Date(task.due_date);
        const formattedDate = date.toISOString().slice(0, 16);
        setValue('due_date', formattedDate);
      }
    }
  }, [task, setValue]);

  const handleFormSubmit = (data) => {
    const formattedData = {
      ...data,
      due_date: data.due_date ? new Date(data.due_date).toISOString() : null
    };
    onSubmit(formattedData);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <div className="task-form-overlay">
      <div className="task-form-container card">
        <div className="task-form-header">
          <h3>{task ? 'Edit Task' : 'Create New Task'}</h3>
          <button
            onClick={handleClose}
            className="btn-icon close-btn"
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="task-form">
          <div className="form-group">
            <label className="form-label">
              <FileText size={16} />
              Task Title
            </label>
            <input
              type="text"
              className={`input ${errors.title ? 'error' : ''}`}
              placeholder="What needs to be done?"
              {...register('title', {
                required: 'Task title is required',
                minLength: {
                  value: 1,
                  message: 'Title must not be empty'
                },
                maxLength: {
                  value: 200,
                  message: 'Title must be less than 200 characters'
                }
              })}
            />
            {errors.title && (
              <span className="form-error">{errors.title.message}</span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">
              <FileText size={16} />
              Description (Optional)
            </label>
            <textarea
              className="input textarea"
              placeholder="Add more details about this task..."
              rows={3}
              {...register('description', {
                maxLength: {
                  value: 1000,
                  message: 'Description must be less than 1000 characters'
                }
              })}
            />
            {errors.description && (
              <span className="form-error">{errors.description.message}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <Flag size={16} />
                Priority
              </label>
              <select
                className="input"
                {...register('priority')}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                <Calendar size={16} />
                Due Date (Optional)
              </label>
              <input
                type="datetime-local"
                className="input"
                {...register('due_date')}
              />
            </div>
          </div>

          {task && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="checkbox"
                  {...register('completed')}
                />
                Mark as completed
              </label>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
            >
              <Save size={16} />
              {task ? 'Update Task' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskForm;
